import { type FC, lazy, useCallback, useEffect, useRef, Suspense, useState } from 'react';
import { Milkdown, useEditor, useInstance } from '@milkdown/react';
import { ProsemirrorAdapterProvider, usePluginViewFactory, useNodeViewFactory } from '@prosemirror-adapter/react';
import type { CollabService } from '@milkdown/plugin-collab';
import type { EditorView } from '@milkdown/kit/prose/view';

import type { BrainfishEditorProps, MediaUploadHandler, ReviewSuggestionsEditorData } from '../types';
import { VariableContentChannelsContext } from '../nodes/variable-content';
import { useEditorTheme } from '../useEditorTheme';
import { useEditorReady, type PostDispatchFn } from '../useEditorReady';
import {
  useDefaultReviewSuggestionSelection,
  useReviewSuggestionBulkActionsRef,
  useReviewSuggestionSelectionSync,
  useReviewSuggestionsLoader,
} from '../plugins/reviewSuggestions';
import { defaultMediaUpload } from '../plugins/runEditorMediaUpload';
import { buildCrepe } from './internal/buildCrepe';
import { registerPlugins } from './internal/registerPlugins';
import { setupCollaboration, setupCollabContentChange, type CollabState } from './internal/setupCollaboration';

interface MilkdownEditorProps extends Omit<BrainfishEditorProps, 'collabConfig'> {
  collabState?: CollabState;
  onGrammarCheck?: (text: string) => Promise<string>;
  onReady?: () => void;
  /** Ref to be set with replaceContent function when editor is ready */
  replaceContentRef?: React.MutableRefObject<((markdown: string) => void) | null>;
  /** Ref to be set with refreshHeadings function when editor is ready */
  refreshHeadingsRef?: React.MutableRefObject<(() => void) | null>;
}

const ReviewSuggestionsViewport = lazy(() =>
  import('../plugins/reviewSuggestions').then((module) => ({ default: module.ReviewSuggestionsViewport })),
);

/**
 * Inner editor component that uses the plugin view factory.
 * Wraps Milkdown with Crepe preset and handles editor creation, plugins,
 * and collaboration binding.
 */
const MilkdownEditorInner: FC<MilkdownEditorProps> = ({
  readOnly = false,
  markdown,
  className,
  theme = 'system',
  collabState,
  onSearchLink,
  onGrammarCheck,
  onUploadImage,
  onUploadVideo,
  onUploadFile,
  onHeadingsChange,
  replaceContentRef,
  refreshHeadingsRef,
  onReady,
  onContentChange,
  issues,
  reviewSuggestionsLoader,
  onReviewSuggestionAccept,
  onReviewSuggestionReject,
  onReviewSuggestionSourceClick,
  onReviewSuggestionResolve,
  onReviewSuggestionsResolved,
  reviewSuggestionBulkActionsRef,
  imageErrorFallbackUrl,
  reviewSuggestionRailTopClassName,
  channels = [],
  selectedChannelId,
}) => {
  const resolvedTheme = useEditorTheme(theme);
  const resolvedThemeRef = useRef(resolvedTheme);
  resolvedThemeRef.current = resolvedTheme;

  const hasCollab = !!collabState;
  const pluginViewFactory = usePluginViewFactory();
  const nodeViewFactory = useNodeViewFactory();

  // Capture initial markdown for collab template (only used once if Y.Doc fragment is empty)
  const initialMarkdownRef = useRef(markdown);
  const channelsRef = useRef(channels);
  channelsRef.current = channels;
  const selectedChannelIdRef = useRef(selectedChannelId);
  selectedChannelIdRef.current = selectedChannelId;
  // Slash menu rebuilds on open via Crepe getGroups → buildMenu; channelsRef is live — do not
  // put channel presence in useEditor deps (empty→loaded recreate triggers flushSync mid-render).
  const editorViewRef = useRef<EditorView | null>(null);

  // Ref to always access latest callback (avoids stale closure in toolbar button)
  const onGrammarCheckRef = useRef(onGrammarCheck);
  onGrammarCheckRef.current = onGrammarCheck;

  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;

  const onUploadImageRef = useRef(onUploadImage);
  onUploadImageRef.current = onUploadImage;

  const onUploadVideoRef = useRef(onUploadVideo);
  onUploadVideoRef.current = onUploadVideo;

  const onUploadFileRef = useRef(onUploadFile);
  onUploadFileRef.current = onUploadFile;

  const uploadImageHandler = useCallback<MediaUploadHandler>(
    (file, onProgress) => onUploadImageRef.current?.(file, onProgress) ?? defaultMediaUpload(file, onProgress),
    [],
  );
  const uploadVideoHandler = useCallback<MediaUploadHandler>(
    (file, onProgress) => onUploadVideoRef.current?.(file, onProgress) ?? defaultMediaUpload(file, onProgress),
    [],
  );
  const uploadFileHandler = useCallback<MediaUploadHandler>(
    (file, onProgress) => onUploadFileRef.current?.(file, onProgress) ?? defaultMediaUpload(file, onProgress),
    [],
  );

  // Health-check issues can get a new array reference each parent render; keep out of useEditor deps.
  const issuesRef = useRef(issues);
  issuesRef.current = issues;

  const [selectedReviewSuggestionId, setSelectedReviewSuggestionId] = useState<string | null>(null);
  const selectedReviewSuggestionIdRef = useRef(selectedReviewSuggestionId);
  selectedReviewSuggestionIdRef.current = selectedReviewSuggestionId;
  const [loadingInstance] = useInstance();
  const onReviewSuggestionSelectRef = useRef<(suggestionId: string | null) => void>();
  onReviewSuggestionSelectRef.current = (suggestionId) => {
    setSelectedReviewSuggestionId((prev) => (prev === suggestionId ? null : suggestionId));
  };

  const onReviewSuggestionResolveRef = useRef(onReviewSuggestionResolve);
  onReviewSuggestionResolveRef.current = onReviewSuggestionResolve;

  const onReviewSuggestionsResolvedRef = useRef(onReviewSuggestionsResolved);
  onReviewSuggestionsResolvedRef.current = onReviewSuggestionsResolved;

  const imageErrorFallbackUrlRef = useRef(imageErrorFallbackUrl);
  imageErrorFallbackUrlRef.current = imageErrorFallbackUrl;

  // When collab is enabled, markdown changes shouldn't recreate the editor
  // (content comes from Y.Doc, not the prop)
  const stableMarkdown = hasCollab ? undefined : markdown;

  // Ref to hold the collab service so we can disconnect it before editor teardown.
  const collabServiceRef = useRef<CollabService | null>(null);
  // When collab is enabled, useEditorReady registers post-dispatch (heading tracking) here
  // instead of wrapping dispatch again — single wrapper lives in setupCollaboration.
  const postDispatchRef = useRef<PostDispatchFn | null>(null);
  const localEditPendingRef = useRef(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const [hasReviewSuggestions, setHasReviewSuggestions] = useState(false);

  // Each editor factory run is a new "generation". A torn-down editor's suggestion
  // fetch can resolve after re-create (readOnly toggle, room switch, StrictMode).
  // We ignore loaded callbacks from any generation other than the latest.
  const editorGenerationRef = useRef(0);
  const onReviewSuggestionsLoadedRef = useRef<(data: ReviewSuggestionsEditorData) => void>();

  // Theme CSS is injected live via useEditorTheme — do not put resolvedTheme in useEditor
  // deps (recreates editor → prosemirror-adapter flushSync mid-render, same as channelsRef).
  useEditor(
    (root) => {
      const generation = (editorGenerationRef.current += 1);
      onReviewSuggestionsLoadedRef.current = ({ reviewSuggestions }) => {
        if (editorGenerationRef.current !== generation) return;
        setHasReviewSuggestions(reviewSuggestions.length > 0);
      };

      const crepe = buildCrepe({
        root,
        defaultValue: stableMarkdown,
        resolvedThemeRef,
        onUploadImage: uploadImageHandler,
        onGrammarCheckRef,
        imageErrorFallbackUrlRef,
        channelsRef,
        editorViewRef,
      });

      registerPlugins(crepe, {
        pluginViewFactory,
        nodeViewFactory,
        readOnly,
        hasCollab,
        onSearchLink,
        onUploadImage: uploadImageHandler,
        onUploadVideo: uploadVideoHandler,
        onUploadFile: uploadFileHandler,
        issues: issuesRef.current,
        onReviewSuggestionSelectRef,
        onReviewSuggestionResolveRef,
        onReviewSuggestionsResolvedRef,
        imageErrorFallbackUrlRef,
      });

      // Collaboration binding
      if (collabState) {
        // Use ref for initial markdown - only applied once if Y.Doc fragment is empty
        setupCollaboration(
          crepe,
          collabState.ydoc,
          collabState.awareness,
          initialMarkdownRef.current || '',
          collabServiceRef,
          postDispatchRef,
          localEditPendingRef,
          onReadyRef,
        );
        // Content change listener for dirty tracking (platform draftText, publish button state)
        setupCollabContentChange(crepe, onContentChangeRef, localEditPendingRef);
      } else if (onContentChange) {
        // Standalone: same markdownUpdated path as collab (listener debounce applies)
        setupCollabContentChange(crepe, onContentChangeRef);
      }

      return crepe.editor;
    },
    [readOnly, stableMarkdown, hasCollab, collabState, pluginViewFactory, nodeViewFactory, onSearchLink],
  );

  useReviewSuggestionsLoader(
    reviewSuggestionsLoader,
    onReviewSuggestionsLoadedRef,
    editorGenerationRef,
    selectedReviewSuggestionIdRef,
  );
  useReviewSuggestionBulkActionsRef(reviewSuggestionBulkActionsRef);

  // Collab: onReady from setupCollaboration after Y.Doc apply; standalone: from useEditorReady.
  useEffect(() => {
    if (!loadingInstance) return;
    setHasReviewSuggestions(false);
    setSelectedReviewSuggestionId(null);
  }, [loadingInstance]);

  useDefaultReviewSuggestionSelection(hasReviewSuggestions, setSelectedReviewSuggestionId);
  useReviewSuggestionSelectionSync(selectedReviewSuggestionId);

  // Expose editor utilities when ready (ProseMirror view for testing, replaceContent for external use, headings for TOC).
  // Returns a callback ref that runs synchronously during commit — before any collab
  // dispatches can fire — eliminating the timing gap that caused "Context not found" errors.
  const editorReadyRef = useEditorReady({
    replaceContentRef,
    refreshHeadingsRef,
    onReady: hasCollab ? undefined : onReady,
    onHeadingsChange,
    collabServiceRef,
    postDispatchRef: hasCollab ? postDispatchRef : undefined,
    editorViewRef,
    selectedChannelIdRef,
  });

  // selectedChannelId is kept in a ref for getHeadings; node views update via context,
  // but TOC extraction only runs on mount/doc changes unless we refresh here.
  useEffect(() => {
    if (!onHeadingsChange) {
      return;
    }
    refreshHeadingsRef?.current?.();
  }, [selectedChannelId, onHeadingsChange]);

  // Container ref is merged with `editorReadyRef` because that one is a
  // callback ref owned by useEditorReady and we can't mutate it.
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      editorReadyRef(node);
    },
    [editorReadyRef],
  );

  if (!reviewSuggestionsLoader && !hasReviewSuggestions) {
    return (
      <div ref={containerRef} className={className} data-testid="brainfish-editor">
        <Milkdown />
      </div>
    );
  }

  return (
    <Suspense fallback={<div>Loading review suggestions...</div>}>
      <ReviewSuggestionsViewport
        className={className}
        containerRef={containerRef}
        selectedReviewSuggestionId={selectedReviewSuggestionId}
        onReviewSuggestionSelect={onReviewSuggestionSelectRef.current}
        onReviewSuggestionAccept={onReviewSuggestionAccept}
        onReviewSuggestionReject={onReviewSuggestionReject}
        onReviewSuggestionSourceClick={onReviewSuggestionSourceClick}
        readOnly={readOnly}
        reviewSuggestionRailTopClassName={reviewSuggestionRailTopClassName}
      >
        <Milkdown />
      </ReviewSuggestionsViewport>
    </Suspense>
  );
};

/**
 * Core editor component wrapping Milkdown with Crepe preset.
 * Handles editor creation, plugins, and collaboration binding.
 */
export const MilkdownEditor: FC<MilkdownEditorProps> = (props) => {
  return (
    <VariableContentChannelsContext.Provider
      value={{ channels: props.channels ?? [], selectedChannelId: props.selectedChannelId }}
    >
      <ProsemirrorAdapterProvider>
        <MilkdownEditorInner {...props} />
      </ProsemirrorAdapterProvider>
    </VariableContentChannelsContext.Provider>
  );
};
